import React, { useContext, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import UserProvider from 'contexts/UserProvider';
import AppRouteProvider from 'contexts/AppRouteProvider';
import ActivePatientProvider from 'contexts/ActivePatientProvider';
import { routes } from 'config/routes';
import logoSvg from 'assets/icons/silver-logo.svg';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route } from 'react-router-dom';
import CacheBuster from '../../CacheBuster';
import {
  changeIsTabActive,
  checkAuthorizationExpiresTimeout,
  fetchDisabledFeatures,
  getAndSetIsSideMenuPinned, setScreenSize,
} from '../../redux_store/reducer/reducers/appSettingsReducer';
import useNotifier from '../Notifications/useNotifier';
import { getUser } from '../../redux_store/reducer/reducers/userReducer/api';
import { REQUEST_STATUS } from '../../redux_store/constants';
import {
  changeCurrentAccountForAlertsNotifications,
  startAlertsConnection,
} from '../../redux_store/reducer/middleware/alertsMiddleware';
import { getIsLogin } from '../../redux_store/reducer/reducers/userReducer/userReducer';
import { AppRouteContext } from '../../contexts';
import { fetchTableSettings } from '../../redux_store/reducer/reducers/tableReducer/api';
import { useBeforeRender } from '../../hooks/useBeforeRender';
import { getAndSetIsInfoPaneExpanded } from '../../redux_store/reducer/reducers/infoPaneReducer';
import ProtectedRoute from './widgets/ProtectedRoute';
import PublicRoute from './widgets/PublicRoute';
import EnvTopBar from './widgets/EnvTopBar';
import Idle from './widgets/Idle';
import { ToastContainer } from 'react-toastify';
import { getAlertTags } from '../../redux_store/reducer/reducers/alertsReducer/api';

const useStyles = makeStyles((theme) => ({
  main: {
    height: '100vh',
    position: 'relative',
    backgroundImage: `url(${logoSvg})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '60%',
    backgroundPosition: '115% 20%',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      width: '100%',
      height: '100%',
      backgroundColor: theme.colors.gray3,
      zIndex: -1,
    },
  },
}));

const ROUTES = Object.values(routes);

const App = () => {
  useNotifier();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isLogin, status, user } = useSelector((state) => state.user);
  const { metadata } = useSelector((state) => state.user);
  const { setAppRoute } = useContext(AppRouteContext);

  useEffect(() => {
    dispatch(getAndSetIsSideMenuPinned());
    dispatch(getAndSetIsInfoPaneExpanded());
    dispatch(fetchDisabledFeatures());
    dispatch(getIsLogin());
    dispatch(checkAuthorizationExpiresTimeout());
    if (isLogin) {
      dispatch(getAlertTags());
      dispatch(getUser());
    }
  }, []);

  useEffect(() => {
    if (isLogin && !user) {
      dispatch(getAlertTags());
      dispatch(getUser());
      dispatch(fetchTableSettings());
      startAlertsConnection();
    }
  }, [isLogin]);

  useEffect(() => {
    if (metadata?.currentAccount?.uuid) {
      dispatch(changeCurrentAccountForAlertsNotifications(metadata?.currentAccount?.uuid));
    }
  }, [metadata?.currentAccount?.uuid]);

  useEffect(() => {
    if (status === REQUEST_STATUS.failed) {
      try {
        setAppRoute({
          path: routes.login.path,
        });
      } catch (e) {
        console.log(e);
      }
    }
  }, [status]);

  useBeforeRender(() => {
    window.addEventListener('error', (e) => {
      if (e) {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div',
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay',
        );
        if (resizeObserverErr)
          resizeObserverErr.className = 'hide-resize-observer';
        if (resizeObserverErrDiv)
          resizeObserverErrDiv.className = 'hide-resize-observer';
      }
    });
  }, []);

  useEffect(() => {
    const updateScreenSize = () => {
      dispatch(setScreenSize({
        height: window.innerHeight,
        width: window.innerWidth,
      }));
    };

    window.addEventListener('resize', updateScreenSize);
    return () => {
      window.removeEventListener('resize', updateScreenSize);
    };
  }, []);

  return (
    <AppRouteProvider>
      <UserProvider>
        <CacheBuster>
          <ActivePatientProvider>
            <ToastContainer />
            {/*<PreventBackNavigation/>*/}
            <_CheckTabActivity />
            <Idle />
            <main className={classes.main}>
              <EnvTopBar />
              <Routes>
                {ROUTES.map((route) => {
                  const { additional } = route;
                  return additional.protected ? (
                    <Route
                      exact
                      path={route.path}
                      element={
                        <ProtectedRoute key={route.path || ''} {...route} />
                      }
                    />
                  ) : (
                    <Route
                      exact
                      path={route.path}
                      element={
                        <PublicRoute key={route.path || ''} {...route} />
                      }
                    />
                  );
                })}
              </Routes>
            </main>
          </ActivePatientProvider>
        </CacheBuster>
      </UserProvider>
    </AppRouteProvider>
  );
};

export default App;

const _CheckTabActivity = () => {
  const dispatch = useDispatch();

  function handleActivity(forcedFlag) {
    if (typeof forcedFlag === 'boolean') {
      return dispatch(changeIsTabActive(forcedFlag));
    }
    return dispatch(changeIsTabActive(!document.hidden));
  }

  function setContentHidden() {
    handleActivity(false);
  }

  function setContentShown() {
    handleActivity(true);
  }

  useEffect(() => {
    window.onfocus = setContentShown;
    document.onvisibilitychange = () => {
      if (document.visibilityState === 'hidden') {
        setContentHidden();
      }
      if (document.visibilityState === 'visible') {
        setContentShown();
      }
    };

    window.addEventListener('focus', setContentShown);
    document.addEventListener('visibilitychange', handleActivity);
    return () => {
      window.removeEventListener('focus', setContentShown);
      document.removeEventListener('visibilitychange', handleActivity);
    };
  }, []);

  return <></>;
};

// function PreventBackNavigation() {
//   const navigate = useNavigate();
//   const location = useLocation();
//
//   useEffect(() => {
//     const handlePopState = () => {
//       navigate(location.pathname, { replace: true });
//     };
//
//     window.addEventListener('popstate', handlePopState);
//     return () => {
//       window.removeEventListener('popstate', handlePopState);
//     };
//   }, [location, navigate]);
//
//   return null;
// }

